<script setup lang="ts">
import { Pencil } from 'lucide';
import { computed, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphField } from '@/components/base/field';
import { SphInput } from '@/components/base/input';
import { SphSelect } from '@/components/base/select';
import { useI18n } from '@/composables/i18n';
import { useTableSort } from '@/composables/tableSort';
import { formatIsoDate } from '@/utils/date';
// Lives under views/federation until #5098 lands SphTableHead, which replaces it everywhere.
import TableSortHeader from '@/views/federation/TableSortHeader.vue';

interface Absence {
    id: number
    title: string
    absenceType: string
    description: string
    longTerm: boolean
    dateFrom: string | null
    dateTo: string | null
    editUrl: string
}

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    rows: Absence[]
}>();

const { i18n } = useI18n();

const ALL_YEARS = 'all';

const search = ref('');
const currentYear = new Date().getFullYear();
// Opens on every year, unlike the Urlaub filter: see FurtherEducationPage.
const year = ref<string>(ALL_YEARS);

/**
 * The years an absence covers, from its start to its end.
 *
 * An absence without an end date is still running - parental leave, a long illness - so it
 * reaches up to the current year rather than ending on the day it began.
 */
function yearsOf(row: Absence): number[] {
    if (row.dateFrom === null) {
        return [];
    }

    const from = Number(row.dateFrom.slice(0, 4));
    const to = row.dateTo === null ? Math.max(from, currentYear) : Number(row.dateTo.slice(0, 4));

    const years = [];
    for (let candidate = from; candidate <= to; candidate++) {
        years.push(candidate);
    }

    return years;
}

const yearOptions = computed(() => {
    const years = new Set<number>([currentYear]);

    props.rows.forEach((row) => yearsOf(row).forEach((rowYear) => years.add(rowYear)));

    const options = [...years]
        .sort((a, b) => b - a)
        .map((candidate) => ({ value: String(candidate), label: String(candidate) }));

    options.unshift({ value: ALL_YEARS, label: i18n.t('alle') });

    return options;
});

function matchesYear(row: Absence): boolean {
    return year.value === ALL_YEARS || yearsOf(row).includes(Number(year.value));
}

function searchableText(row: Absence): string {
    return [
        row.title,
        row.absenceType,
        row.description,
        // The long-term column renders a translated "Ja", and the DataTables search matched it.
        row.longTerm ? i18n.t('Ja') : '',
        formatIsoDate(row.dateFrom),
        formatIsoDate(row.dateTo),
    ].join(' ').toLowerCase();
}

const filtered = computed(() => {
    const term = search.value.trim().toLowerCase();

    return props.rows.filter((row) => {
        if (!matchesYear(row)) {
            return false;
        }

        return term === '' || searchableText(row).includes(term);
    });
});

const { sortKey, sortDirection, toggleSort, sorted } = useTableSort(filtered, {
    title: (row) => row.title,
    absenceType: (row) => row.absenceType,
    description: (row) => row.description,
    longTerm: (row) => (row.longTerm ? 1 : 0),
    // ISO dates sort chronologically as plain strings; an open end sorts as an empty string,
    // so it leads the ascending order.
    dateFrom: (row) => row.dateFrom,
    dateTo: (row) => row.dateTo,
});

defineExpose({ filtered, sorted, search, year, yearOptions, sortKey, sortDirection, toggleSort });
</script>

<template>
    <div>
        <div class="kgr-row kgr-postfix kgr-postfix--between items-end">
            <SphField :label="i18n.t('Jahr')">
                <SphSelect
                    v-model="year"
                    :options="yearOptions"
                    :null-option="false"
                />
            </SphField>
            <SphInput
                v-model="search"
                :placeholder="i18n.t('Suche in dieser Liste...')"
            />
        </div>

        <div class="kgr-row">
            <table class="kgr-table kgr-table--block kgr-table--striped table-groups">
                <thead>
                    <tr class="kgr-table__row">
                        <TableSortHeader
                            :label="i18n.t('Bezeichnung')"
                            sort-key="title"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Art')"
                            sort-key="absenceType"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Notiz')"
                            sort-key="description"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Langfristige Abwesenheit')"
                            sort-key="longTerm"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Beginn')"
                            sort-key="dateFrom"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                        <TableSortHeader
                            :label="i18n.t('Ende')"
                            sort-key="dateTo"
                            :active-key="sortKey"
                            :direction="sortDirection"
                            @sort="toggleSort"
                        />
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in sorted"
                        :key="row.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--center">
                                <div class="kgr-postfix__fluid">
                                    <a
                                        :href="row.editUrl"
                                        class="kgr-link kgr-link--unobtrusive"
                                    >{{ row.title }}</a>
                                </div>
                                <div class="kgr-postfix__fixed">
                                    <SphButton
                                        :icon="Pencil"
                                        as="a"
                                        :href="row.editUrl"
                                        :title="i18n.t('Bearbeiten')"
                                        variant="ghost"
                                        size="sm"
                                        class="kgr-table__action"
                                    />
                                </div>
                            </div>
                        </td>
                        <td class="kgr-table__cell">{{ row.absenceType }}</td>
                        <td class="kgr-table__cell">{{ row.description }}</td>
                        <td class="kgr-table__cell">{{ row.longTerm ? i18n.t('Ja') : '' }}</td>
                        <td class="kgr-table__cell">{{ formatIsoDate(row.dateFrom) || '-' }}</td>
                        <td class="kgr-table__cell">{{ formatIsoDate(row.dateTo) || '-' }}</td>
                    </tr>
                    <tr
                        v-if="sorted.length === 0"
                        class="kgr-table__row"
                    >
                        <td
                            class="kgr-table__cell"
                            colspan="6"
                        >
                            {{ rows.length === 0 ? i18n.t('Keine Abwesenheiten vorhanden') : i18n.t('Keine Datensätze vorhanden') }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
